import { useEffect, useState } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../lib/firebase";
import { useAuth } from "../../contexts/AuthContext";
import { Save, Upload, User, MapPin, Music, Globe, Instagram, Twitter, Youtube, AlertCircle, CheckCircle } from "lucide-react";

const GENRES = ["House", "Techno", "Hip-Hop", "R&B", "Jazz", "Electronic", "Reggae", "Afrobeats", "Latin", "Rock", "Pop", "Indie", "Classical", "Other"];

export default function DashboardProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ displayName: "", bio: "", location: "", genres: [], websiteURL: "", epkURL: "", socials: { instagram: "", twitter: "", youtube: "", soundcloud: "", mixcloud: "", twitch: "" } });
  const [photoFile, setPhotoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [photoURL, setPhotoURL] = useState("");
  const [coverURL, setCoverURL] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { loadProfile(); }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    try {
      const creatorDoc = await getDoc(doc(db, "creators", user.uid));
      if (creatorDoc.exists()) {
        const data = creatorDoc.data();
        setForm({
          displayName: data.displayName || "",
          bio: data.bio || "",
          location: data.location || "",
          genres: data.genres || [],
          websiteURL: data.websiteURL || "",
          epkURL: data.epkURL || "",
          socials: { instagram: "", twitter: "", youtube: "", soundcloud: "", mixcloud: "", twitch: "", ...(data.socials || {}) },
        });
        setPhotoURL(data.photoURL || "");
        setCoverURL(data.coverURL || "");
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const uploadFile = (file, path, key) => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const task = uploadBytesResumable(storageRef, file);
      task.on("state_changed",
        (snap) => setUploadProgress((p) => ({ ...p, [key]: Math.round((snap.bytesTransferred / snap.totalBytes) * 100) })),
        reject,
        async () => { resolve(await getDownloadURL(task.snapshot.ref)); }
      );
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      let finalPhotoURL = photoURL;
      let finalCoverURL = coverURL;

      if (photoFile) {
        finalPhotoURL = await uploadFile(photoFile, `users/${user.uid}/profile/${Date.now()}_photo`, "photo");
        setPhotoURL(finalPhotoURL);
      }
      if (coverFile) {
        finalCoverURL = await uploadFile(coverFile, `users/${user.uid}/profile/${Date.now()}_cover`, "cover");
        setCoverURL(finalCoverURL);
      }

      const slug = form.displayName.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");
      await setDoc(doc(db, "creators", user.uid), {
        ...form,
        uid: user.uid,
        slug,
        photoURL: finalPhotoURL,
        coverURL: finalCoverURL,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const toggleGenre = (g) => {
    setForm((f) => ({ ...f, genres: f.genres.includes(g) ? f.genres.filter((x) => x !== g) : [...f.genres, g] }));
  };

  if (loading) return <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-[#00ff77] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="dashboard-profile" className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-bold text-white">Edit Profile</h2>

      <form onSubmit={handleSave} className="space-y-5">
        {/* Cover / Photo */}
        <div className="relative h-36 bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
          {coverURL && <img src={coverURL} alt="" className="w-full h-full object-cover opacity-60" />}
          <div className="absolute inset-0 flex items-center justify-center">
            <label className="cursor-pointer flex items-center gap-2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full hover:bg-black/80 transition-colors">
              <Upload size={12} /> Upload Cover
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setCoverFile(e.target.files[0])} />
            </label>
          </div>
          {uploadProgress.cover > 0 && uploadProgress.cover < 100 && (
            <div className="absolute bottom-0 left-0 h-1 bg-[#00ff77]" style={{ width: `${uploadProgress.cover}%` }} />
          )}
        </div>

        <div className="flex items-end gap-4">
          <div className="relative w-20 h-20 rounded-2xl bg-[#111] border border-white/10 overflow-hidden flex-shrink-0">
            {photoURL ? (
              <img src={photoURL} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><User size={24} className="text-zinc-600" /></div>
            )}
            <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
              <Upload size={14} className="text-white" />
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files[0])} />
            </label>
          </div>
          <div className="flex-1">
            <input data-testid="display-name-input" type="text" placeholder="Display Name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50" />
          </div>
        </div>

        <textarea data-testid="bio-input" placeholder="Bio — tell your story..." value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50 resize-none" />

        <div className="grid grid-cols-2 gap-3">
          <input type="text" placeholder="Location (City, Country)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
            className="bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50" />
          <input type="url" placeholder="Website URL" value={form.websiteURL} onChange={(e) => setForm({ ...form, websiteURL: e.target.value })}
            className="bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50" />
        </div>

        <input data-testid="epk-url-input" type="url" placeholder="EPK Link (Electronic Press Kit URL)" value={form.epkURL} onChange={(e) => setForm({ ...form, epkURL: e.target.value })}
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#00ff77]/50" />

        {/* Genres */}
        <div>
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2 block">Genres</label>
          <div className="flex flex-wrap gap-2">
            {GENRES.map((g) => (
              <button key={g} type="button" onClick={() => toggleGenre(g)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.genres.includes(g) ? "bg-[#00ff77]/20 border-[#00ff77]/50 text-[#00ff77]" : "bg-transparent border-white/10 text-zinc-400 hover:border-white/30"}`}>
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* Socials */}
        <div>
          <label className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-3 block">Social Links</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { key: "instagram", placeholder: "Instagram URL", icon: Instagram },
              { key: "twitter", placeholder: "Twitter/X URL", icon: Twitter },
              { key: "youtube", placeholder: "YouTube URL", icon: Youtube },
              { key: "soundcloud", placeholder: "SoundCloud URL", icon: Music },
              { key: "mixcloud", placeholder: "Mixcloud URL", icon: Music },
              { key: "twitch", placeholder: "Twitch URL", icon: Globe },
            ].map(({ key, placeholder, icon: Icon }) => (
              <div key={key} className="relative">
                <Icon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input type="url" placeholder={placeholder} value={form.socials[key]}
                  onChange={(e) => setForm({ ...form, socials: { ...form.socials, [key]: e.target.value } })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl pl-8 pr-4 py-2 text-white text-xs focus:outline-none focus:border-[#00ff77]/50" />
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
            <AlertCircle size={14} className="text-red-400" />
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        <button data-testid="save-profile-btn" type="submit" disabled={saving}
          className="flex items-center gap-2 bg-[#00ff77] text-black font-bold px-6 py-2.5 rounded-full hover:bg-[#00e66b] transition-colors disabled:opacity-50">
          {saved ? <><CheckCircle size={16} /> Saved!</> : saving ? "Saving..." : <><Save size={16} /> Save Profile</>}
        </button>
      </form>
    </div>
  );
}
